import React, { useEffect, useState, useRef } from 'react';
import { Lock, Eye } from 'lucide-react';

/**
 * IdlePrivacyLock
 * ----------------
 * HIPAA-friendly screen-locking without log-out. After 3 minutes of no user
 * activity (mouse, keyboard, touch), blurs the entire app and shows a
 * "tap to resume" overlay. Bypasses the full re-auth flow but prevents a
 * shoulder-surfer from reading patient data on an unattended iPad.
 *
 * Feb 16, 2026 — UX polish #4.
 *
 * Persistable: set `localStorage.writeright_idle_lock_disabled = "1"` to
 * disable (e.g., for live demos or kiosk mode).
 */
const IDLE_TIMEOUT_MS = 3 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'mousemove'];

const IdlePrivacyLock = () => {
  const [locked, setLocked] = useState(false);
  const timerRef = useRef(null);
  const disabled = (() => {
    try { return localStorage.getItem('writeright_idle_lock_disabled') === '1'; }
    catch { return false; }
  })();

  useEffect(() => {
    if (disabled) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setLocked(true), IDLE_TIMEOUT_MS);
    };

    const onActivity = () => {
      if (!locked) resetTimer();
    };

    for (const evt of ACTIVITY_EVENTS) window.addEventListener(evt, onActivity, { passive: true });
    resetTimer();

    return () => {
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, onActivity);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [locked, disabled]);

  if (disabled || !locked) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-background/95 backdrop-blur-xl flex items-center justify-center cursor-pointer animate-in fade-in duration-200"
      onClick={() => setLocked(false)}
      data-testid="idle-privacy-lock"
      role="dialog"
      aria-label="Screen locked — tap to resume"
    >
      <div className="text-center max-w-md px-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
          <Lock className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Screen Privacy Lock</h2>
        <p className="text-sm text-muted-foreground mb-4">
          App was idle for 3 minutes. Patient information has been blurred for HIPAA privacy.
        </p>
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm"
          data-testid="idle-privacy-lock-resume"
        >
          <Eye className="w-4 h-4" />
          Tap anywhere to resume
        </div>
        <p className="text-[11px] text-muted-foreground/70 italic mt-4">
          No re-login required. Your session is preserved.
        </p>
      </div>
    </div>
  );
};

export default IdlePrivacyLock;
